/* eslint-disable */
import React, { useEffect, useState } from "react";
import PropTypes from "prop-types";

import { FormLayout, FormLayoutSection, Section } from "@fluid/layouts";
import { useWidgetCssLoad } from "@dbank-ui-lib/widget-utils";

import { DEFAULT_LOCALE, NO_OP_FN } from "./Constants";
import LoginMessage from "./components/LoginMessage";

import "./styles/login.scss";
import LoginOverlay from "./components/LoginOverlay";
import LoginLinks from "./components/LoginLinks";
import LoginForm from "./components/form/LoginForm";
import LoginPromotions from "./components/LoginPromotions";
import LoginFdicMsg from "./components/LoginFdicMsg";
import LoginError from "./components/LoginError";
import LoginBrowserCompat from "./components/LoginBrowserCompat";
import LoginBranding from "./components/LoginBranding";

import LoginContext from "./context/LoginContext";
import { saveUserName } from "./utils/RememberMeUtils";
import { isUnSupportedBrowser } from "./utils/LoginUtils";
import LoginUnsupported from "./components/LoginUnsupported";
import LoginElementsHeader from "./components/LoginElementsHeader";

const baseCls = "dbk-login";
const isIE11 = document.documentMode && document.documentMode === 11;

function LoginWidget(props) {
  const {
    sourceUrl,
    supportedLocales,
    fdicConfig,
    loginConfig,
    mode,
    locale: localeParam,
    theme,
    legacyLoginConfig,
    uiConfig,
    onWidgetInit,
  } = props;

  const { isOAuth2Flow } = legacyLoginConfig ?? {};

  const locale = localeParam ?? "en_US";
  let { onLocaleSelect, onLinkSelect, onLoginSubmit } = props;
  onLocaleSelect = onLocaleSelect ?? NO_OP_FN;
  onLinkSelect = onLinkSelect ?? NO_OP_FN;
  onLoginSubmit = onLoginSubmit ?? NO_OP_FN;

  const [context, setContext] = useState({ onWidgetInit });
  const [values, setValues] = useState({
    username: "",
    password: "",
  });

  const hasFDICdata = () => {
    const { loginPageComplianceLogo, loginPageInsuredStatement } =
      fdicConfig || {};

    return (
      !!fdicConfig && !!loginPageComplianceLogo && !!loginPageInsuredStatement
    );
  };
  const handlePasswordChange = (password) => {
    setValues({
      ...values,
      password,
    });

    if (!context.passwordValueChanged) {
      setContext({ ...context, passwordValueChanged: true });
    }
  };
  const handleUsernameChange = (username) => {
    setValues({
      ...values,
      username,
    });
  };
  const handleSubmit = (e) => {
    e.preventDefault();
    const { username, password } = values;

    if (username && password) {
      onLoginSubmit(values, e);
      setContext({ ...context, passwordValueChanged: false });
      saveUserName(username, loginConfig);
    }
  };

  const isInteractive = mode !== "ADMIN";
  const { isCssLoading } = useWidgetCssLoad(sourceUrl, "dbk-login-widget-css");

  if (isCssLoading) {
    return false;
  }

  const { cookieDisabled } = context;
  const showForm = !cookieDisabled;
  const showPromotions = !cookieDisabled && !isOAuth2Flow;
  const showLinks = !cookieDisabled && !isOAuth2Flow;
  const showBranding = isInteractive ? true : !isIE11;

  if (isUnSupportedBrowser(loginConfig, isIE11)) {
    return (
      <LoginUnsupported
        loginConfig={loginConfig}
        baseCls={baseCls}
        theme={theme}
      />
    );
  }

  const { hasText, hasImage } = context;
  const contextClasses = `${baseCls} ${
    hasText || hasImage ? "has-message" : ""
  } ${isIE11 ? "is-ie-11" : ""} ${isInteractive ? "is-interactive" : ""}`;

  return (
    <LoginContext.Provider value={{ context, setContext }}>
      <Section contextCls={contextClasses} indentContent={false}>
        {isInteractive && (
          <LoginBrowserCompat uiConfig={uiConfig} loginConfig={loginConfig} />
        )}
        {showBranding && (
          <LoginBranding
            theme={theme}
            config={uiConfig}
            isInteractive={isInteractive}
          />
        )}

        {!isOAuth2Flow && (
          <LoginMessage
            theme={theme}
            contextCls={`${baseCls}__msg-container`}
            loginConfig={loginConfig}
            locale={locale}
            isInteractive={isInteractive}
          />
        )}

        <div className={`${baseCls}__login-elements-container`}>
          {!isInteractive && (
            <LoginOverlay contextCls={`${baseCls}__overlay`} />
          )}

          <LoginElementsHeader
            contextCls={baseCls}
            isInteractive={isInteractive}
            loginConfig={loginConfig}
            onLocaleSelect={onLocaleSelect}
            theme={theme}
            supportedLocales={supportedLocales}
            legacyLoginConfig={legacyLoginConfig}
          />
          <form
            id="login-form"
            action="/"
            onSubmit={handleSubmit}
            className={`${baseCls}__login-form`}
            autoComplete="off"
          >
            <FormLayout
              shouldPadContent={false}
              contextCls={`${baseCls}__login-form-container`}
            >
              <FormLayoutSection
                shouldIndentContent={false}
                contextCls={`${baseCls}__form-section`}
              >
                {hasFDICdata() && <LoginFdicMsg fdicConfig={fdicConfig} />}
                <LoginError legacyLoginConfig={legacyLoginConfig} />
                {showForm && (
                  <LoginForm
                    loginConfig={loginConfig}
                    theme={theme}
                    locale={locale}
                    isInteractive={isInteractive}
                    values={values}
                    onUsernameChange={handleUsernameChange}
                    onPasswordChange={handlePasswordChange}
                    isOAuth2Flow={isOAuth2Flow}
                  />
                )}
                {showPromotions && (
                  <LoginPromotions
                    legacyLoginConfig={legacyLoginConfig}
                    contextCls={`${baseCls}__promotions`}
                  />
                )}
                {showLinks && (
                  <LoginLinks
                    contextCls={`${baseCls}__links`}
                    loginConfig={loginConfig}
                    locale={locale}
                    isInteractive={isInteractive}
                    onLinkSelect={onLinkSelect}
                    legacyLoginConfig={legacyLoginConfig}
                  />
                )}
              </FormLayoutSection>
            </FormLayout>
          </form>
        </div>
      </Section>
    </LoginContext.Provider>
  );
}

LoginWidget.defaultProps = {
  locale: DEFAULT_LOCALE,
  onLocaleSelect: NO_OP_FN,
  onLinkSelect: NO_OP_FN,
};

LoginWidget.propTypes = {
  sourceUrl: PropTypes.string.isRequired,
  fdicConfig: PropTypes.object.isRequired,
  loginConfig: PropTypes.object.isRequired,
  supportedLocales: PropTypes.array,
  mode: PropTypes.string,
  locale: PropTypes.string,
  theme: PropTypes.object,

  uiConfig: PropTypes.object,
  legacyLoginConfig: PropTypes.object,

  onLocaleSelect: PropTypes.func,
  onLinkSelect: PropTypes.func,
  onLoginSubmit: PropTypes.func,
};

export default LoginWidget;
